import {t} from 'i18next';
import './RedistributeDocuments.scss';
import {Box, Checkbox, Grid, Typography} from '@mui/material';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import React, {useEffect, useMemo, useState} from 'react';
import {ReactComponent as CheckedIcon} from '../../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../../src/assets/images/icon-unchecked.svg';
import {ReactComponent as InfoIcon} from '../../../../../src/assets/images/info-icon.svg';
import ArrCheckboxList from '../../../common/arr-checkbox-list/ArrCheckboxList';
import {isEmpty} from 'lodash';
import {SourceTypeCode, ToastType} from '../../../../shared/constants';
import {
  useCallDocumentReassign,
  useGetReassignUserList,
} from '../../../../hooks/assignments/useAssignments';
import {
  DocumentAssignmentReqPayload,
  ReviewerAssignmentResponse,
  ReviewerUser,
  SelectedReviewer,
} from '../../../../api/types/reportingApi.type';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ToastMessageConfigType} from '../../../../api/types/ingestionApi.type';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {Trans} from 'react-i18next';
import ArrNumericInput from '../../../common/arr-numeric-input/ArrNumericInput';

type RedistributeDocuments = {
  showDrawer: boolean;
  onSuccess: (messge?: string) => void;
  onClose: () => void;
  sourceType: SourceTypeCode;
  assignmentId: number;
  currentReviewerId: string;
  redistributeCount: number;
};

const RedistributeDocuments: React.FC<RedistributeDocuments> = ({
  onClose,
  onSuccess,
  sourceType,
  assignmentId,
  currentReviewerId,
  redistributeCount,
  showDrawer,
}) => {
  const [documentCount, setDocumentCount] = useState(redistributeCount);
  const [reviewerName, setReviewerName] = useState('');
  const [currentReviewerName, setCurrentReviewerName] = useState('');
  const [familyDistribution, setFamilyDistribution] = useState(false);
  const [selectedReviewerList, setSelectedReviewrList] = useState<
    SelectedReviewer[]
  >([]);
  const [listReviewerResponse, setListReviewerResponse] = useState<
    ReviewerUser[]
  >([]);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const {mutate: getReassignUserList, isPending: isReassignUserListLoading} =
    useGetReassignUserList(
      (response: ReviewerAssignmentResponse) => {
        const currentReviewer = response.listReviewerResponse.find(
          (reviewer) => reviewer.reviewerId === Number(currentReviewerId ?? '0')
        );
        if (currentReviewer) {
          setCurrentReviewerName(
            currentReviewer.reviewerName.split('(')[0].trim()
          );
        }
        const filteredList = response.listReviewerResponse.filter(
          (reviewer) => reviewer.reviewerId !== Number(currentReviewerId ?? '0')
        );
        setListReviewerResponse(filteredList);
      },
      (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            error?.response?.data?.title ??
            t('reports.redistributeDocuments.reviewerloadFaield'),
        });
      }
    );

  const {mutate: callDocumentReassign, isPending: isDocumentReassignLoading} =
    useCallDocumentReassign(
      () => {
        onSuccess(t('reports.redistributeDocuments.actionSavedSuccess'));
      },
      (error: any) => {
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            error?.response?.data?.title ??
            t('reports.redistributeDocuments.actionSaveFailed'),
        });
      }
    );

  useEffect(() => {
    if (showDrawer && !isReassignUserListLoading) {
      getReassignUserList({
        sourceType,
        assignmentId,
        selecteddoc: redistributeCount,
      });
    }
  }, [showDrawer]);

  const {displayReviewerList, displayReviewListWithDetail} = useMemo(() => {
    let displayReviewListWithDetail: ReviewerUser[] = [];

    if (isEmpty(reviewerName)) {
      displayReviewListWithDetail = [...listReviewerResponse];
    } else {
      displayReviewListWithDetail = listReviewerResponse.filter((reviewer) =>
        reviewer.reviewerName.includes(reviewerName)
      );
    }
    const displayReviewerList: {label: string; value: boolean}[] =
      displayReviewListWithDetail.map((reviewer) => ({
        label: reviewer.reviewerName,
        value: false,
      }));
    return {displayReviewListWithDetail, displayReviewerList};
  }, [listReviewerResponse, reviewerName]);

  const onSaveClick = () => {
    if (documentCount > redistributeCount) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message: t('reports.redistributeDocuments.invalidDocumentCountError', {
          maxCount: redistributeCount,
        }),
      });
      return;
    }
    const payload: DocumentAssignmentReqPayload = {
      listReviewers: selectedReviewerList,
      assignmentID: assignmentId.toString(),
      oldReviewerID: Number(currentReviewerId),
      noOfDocToDistribute: documentCount.toString(),
      overrideFamilies: familyDistribution,
    };
    callDocumentReassign(payload);
  };

  const filterReviewer = (reviewerName: string) => {
    setReviewerName(reviewerName);
  };

  if (isReassignUserListLoading) return <ArrLoadingSpinner isOpen overlay />;

  const handleReviewerChange = (checked: boolean[]) => {
    const checkedFields = [];
    for (let i = 0; i < checked.length; i++) {
      if (checked[i] === true) {
        checkedFields.push({
          reviewerId: displayReviewListWithDetail[i].reviewerId.toString(),
        });
      }
    }
    setSelectedReviewrList(checkedFields);
  };

  return (
    <ArrDrawer
      size="small"
      side="right"
      transition={300}
      showButton={false}
      showHeader={false}
      isOpen={showDrawer}
      className="redistribute-documents-drawer"
      ariaLabel="redistribute documents drawer"
      onClose={onClose}
    >
      <Box className="redisctribute-documents">
        <Box className="header">
          <Typography
            className="typo-text headline3_size"
            data-testid={`redistribute-documents-title`}
          >
            {t('reports.redistributeDocuments.title')}
          </Typography>
          <Box className="action-buttons-box">
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={onClose}
              disabled={false}
              dataTestId="redistribute-documents-cancel-button"
            >
              {t('common.cancel')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="medium"
              type="submit"
              disabled={isEmpty(selectedReviewerList) || !documentCount}
              onClick={onSaveClick}
              dataTestId="save-button"
            >
              {t('common.save')}
            </ArrButton>
          </Box>
        </Box>
        <Box className="content">
          <Box className="document-count-input">
            <Typography
              className="typo-text body2_size"
              data-testid={`redistribute-documents-reviewer-count-input`}
            >
              {t('reports.redistributeDocuments.documentCountInput', {
                reviewerName: currentReviewerName,
                count: redistributeCount,
              })}
            </Typography>
            <ArrNumericInput
              minWidth="100px"
              sizeVariant="medium"
              value={documentCount}
              onChange={(e) => setDocumentCount(e!)}
              dataTestId="document-count"
              aria-label="document count"
              showPercentage={false}
            />
          </Box>
          <Box className="checkbox-input">
            <Box className="checkbox-label">
              <Typography
                className="typo-text subhead4_size label"
                data-testid={`redistribute-documents-family-distribution-checkbox`}
              >
                {t('reports.redistributeDocuments.familyDistributionCheckbox')}
                &nbsp;
              </Typography>
              <ArrTooltipWrapper
                title={t(
                  'reports.redistributeDocuments.familyDistributionCheckboxTitle'
                )}
                trigger={
                  <InfoIcon
                    className="info-icon info-icon-small"
                    data-testid="family-destribution-icon"
                    tabIndex={0}
                    aria-label={t('common.infoTooltip')}
                  />
                }
              >
                <Trans
                  i18nKey={t(
                    'reports.redistributeDocuments.familyDistributionCheckboxInfo'
                  )}
                ></Trans>
              </ArrTooltipWrapper>
            </Box>
            <Checkbox
              data-testid="redistribute-documents-family-distribution-checkbox"
              checked={familyDistribution}
              size="small"
              icon={<UnCheckedIcon />}
              checkedIcon={<CheckedIcon />}
              aria-labelledby={t(
                'reports.redistributeDocuments.familyDistributionCheckbox'
              )}
              onChange={(e) => setFamilyDistribution(e.target.checked)}
            />
          </Box>
          <Typography
            className="typo-text body2_size"
            data-testid={`redistribute-documents-reviewer-info`}
          >
            {t('reports.redistributeDocuments.reviewerInfo')}
          </Typography>
          <Grid
            className="reviewer-input-container"
            container
            columnSpacing={2}
            rowSpacing={1.5}
          >
            <Grid size={{xs: 12, sm: 6}}>
              <Typography
                className="typo-text subhead4_size label"
                data-testid={`redistribute-documents-reviewer-label`}
              >
                {t('reports.redistributeDocuments.reviewerLabel')}
              </Typography>
            </Grid>
            <Grid size={{xs: 12, sm: 6}}>
              <ArrTextField
                sizeVariant="medium"
                value={reviewerName}
                onChange={(e) => filterReviewer(e.target.value)}
                placeholder={t(
                  'reports.redistributeDocuments.reviewerPlaceholder'
                )}
                dataTestId="redistributed-doc-reviewer-search"
                aria-label={t('reports.redistributeDocuments.reviewerLabel')}
              />
            </Grid>
          </Grid>
        </Box>
        <Box className="reviewer-list">
          <ArrCheckboxList
            childLabels={displayReviewerList}
            onChange={handleReviewerChange}
            size="small"
            showSelectAll={false}
          />
        </Box>
      </Box>
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title ?? ''}
        />
      )}
      {isDocumentReassignLoading && <ArrLoadingSpinner isOpen overlay />}
    </ArrDrawer>
  );
};

export default RedistributeDocuments;
